import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Store as StoreIcon,
  LocationOn,
  Phone,
  LocalShipping,
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';
import { isStoreAdmin } from '../../utils/roles';

const StoreAdminDashboard = () => {
  const { state, actions } = useApp();
  const { user } = state;

  const [storeDetails, setStoreDetails] = useState(null);
  const [storeLoading, setStoreLoading] = useState(true);

  const storeScoped = isStoreAdmin(user?.role);
  const allStoresScope = user?.role === 'admin';

  useEffect(() => {
    if (!state.isAuthenticated || !user) return;

    if (allStoresScope) {
      setStoreLoading(false);
      return;
    }

    if (storeScoped && user.linkedStore) {
      fetchStoreDetails();
    } else {
      setStoreLoading(false);
    }
  }, [state.isAuthenticated, user?.linkedStore, user?.role]);

  const fetchStoreDetails = async () => {
    try {
      setStoreLoading(true);
      const response = await api.get(`/stores/${user.linkedStore}`);
      if (response.data.success) {
        setStoreDetails(response.data.store);
      }
    } catch (error) {
      console.error('Error fetching store details:', error);
      actions.showSnackbar('Error fetching store details', 'error');
    } finally {
      setStoreLoading(false);
    }
  };

  if (allStoresScope) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Use the sidebar to manage orders, delivery, and reports across all stores.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
          Store Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your store and view orders
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <StoreIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">My Store Details</Typography>
          </Box>

          {storeLoading ? (
            <CircularProgress />
          ) : storeDetails ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Store Name:</strong> {storeDetails.storeName}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Branch ID:</strong> {storeDetails.branchId}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <LocationOn sx={{ fontSize: 16, mr: 1 }} />
                  <strong>Address:</strong> {storeDetails.address}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <Phone sx={{ fontSize: 16, mr: 1 }} />
                  <strong>Contact:</strong> {storeDetails.contactNo}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <LocalShipping sx={{ fontSize: 16, mr: 1 }} />
                  <strong>Delivery Radius:</strong> {storeDetails.deliveryRadius} km
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Status:</strong>
                  <Chip
                    label={storeDetails.status ? 'Active' : 'Inactive'}
                    color={storeDetails.status ? 'success' : 'error'}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="warning">Store details not found</Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default StoreAdminDashboard;
